;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { formatNumber } from './number'

/**
 * Turn a word into its plural form based on the count.
 * Provide an explicit plural version of the word for irregular nouns.
 *
 * @example
 * // Regular noun example
 * pluralize({ word: 'cat', count: 1 }); // Output: '1 cat'
 * pluralize({ word: 'cat', count: 2 }); // Output: '2 cats'
 *
 * @example
 * // Irregular noun example
 * pluralize({ word: 'mouse', count: 1, plural: 'mice' }); // Output: '1 mouse'
 * pluralize({ word: 'mouse', count: 2, plural: 'mice' }); // Output: '2 mice'
 *
 * @example
 * // Exclude the count from the output
 * pluralize({ word: 'cat', count: 2, excludeCount: true }); // Output: 'cats'
 * pluralize({ word: 'cat', count: 2, excludeCount: false }); // Output: '2 cats'
 */
export function pluralize({
  word,
  count,
  plural = `${word}s`,
  excludeCount = false,
}: {
  word: string
  count: number
  /**
   * Plural form of the word
   * @default `${word}s`
   */
  plural?: string
  /**
   * Exclude the count from the output
   * @default false
   * @example
   *
   * pluralize({ word: 'cat', count: 2, excludeCount: true }); // Output: 'cats'
   * pluralize({ word: 'cat', count: 2, excludeCount: false }); // Output: '2 cats'
   */
  excludeCount?: boolean
}) {
  const noun = count === 1 ? word : plural

  return excludeCount ? noun : `${formatNumber({ value: count })} ${noun}`
}

/**
 * Conjugate a verb in the third person singular form based on the count.
 *
 * @param verb - The verb to conjugate (base form, e.g., 'contain').
 * @param count - The subject count to determine singular/plural form.
 * @param thirdPersonSingular - The third person singular form of the verb.
 * @returns The conjugated verb.
 *
 * @example
 * conjugateThirdPersonVerb({ verb: 'contain', count: 1 }); // Output: 'contains'
 * conjugateThirdPersonVerb({ verb: 'contain', count: 2 }); // Output: 'contain'
 */
export function conjugateThirdPersonVerb({
  verb,
  count,
  thirdPersonSingular = `${verb}s`,
}: {
  verb: string
  count: number
  thirdPersonSingular?: string
}) {
  return count === 1 ? thirdPersonSingular : verb
}

/**
 * Formats a list of items into a string with a specified conjunction.
 *
 * @param items - An array of strings or numbers to be formatted.
 * @param conjunction - The conjunction to use between the last two items ('or' or 'and').
 * @returns A string with the items separated by commas and the specified conjunction before the last item.
 *
 * @example
 * ```typescript
 * formatListWithConjunction(['cat', 'dog', 'horse'], {conjunction: 'or'})
 * // Returns: 'cat, dog or horse'
 * ```
 */
export function formatListWithConjunction(
  items: Array<string | number>,
  { conjunction }: { conjunction: 'or' | 'and' },
) {
  const lastItem = items.at(-1)
  if (!lastItem) return ''
  if (items.length === 1) return String(lastItem)
  const firstItems = items.slice(0, -1)
  return `${firstItems.join(', ')} ${conjunction} ${lastItem}`
}

/**
 * A function that mimics the slugify function in canary, which doesn't match the standard js libraries
 * - normalizes a string and replaces all accented characters with standard equivalent
 * - mirrors python slugify in encoding and then decoding the string to cover non-standard characters (this will most likely never be used)
 * - strips '-','_' and space at beginning and end of string
 * - removes non-alphanumeric characters - apostrophes, periods, and commas etc.
 * - replaces double spaces with single spaces
 * - replaces all single spaces with '-'
 * @param value - a string value for the category name
 * @returns a slugified version of the category name, identical to the one outputted by the Python slugify in canary
 */
export function slugify(value: string) {
  const normalizedString = value
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036F]/g, '')
  const encodedString = normalizedString.split('')
  const encodedArray = encodedString.map((letter) => letter.charCodeAt(0))
  const decodedString = String.fromCharCode(...encodedArray)

  return decodedString
    .toLowerCase()
    .trim()
    .replaceAll(/[-_]$/g, '')
    .replaceAll(/^[-_]/g, '')
    .replaceAll(/[^\w\s-]/g, '')
    .replaceAll(/[\s]{2}/g, ' ')
    .replaceAll(' ', '-')
}
